import { Injectable, inject, signal } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { Observable, catchError, map, switchMap, tap, throwError } from 'rxjs';
import { ContactFormStore, ContactFormValue } from '../contact/contact-form.store';
import { SignupAddress, SignupAddressStore } from '../address/signup-address.store';
import {
  CustomerIntakeRequest, CustomerIntakeResponse, CustomerIntakeService, CustomerIntakeTcpa, IntakeOwnerRelationship,
  SubmitCustomerIntakeResponse, toE164,
} from './customer-intake.service';
import { TcpaBrowserMetadataService } from './tcpa-browser-metadata.service';
import { Rc1EstimateStore } from '../direct-quote/rc1-estimate.store';

/** Saved contact responses plus the address they were entered for. */
export interface ContactSubmission extends ContactFormValue {
  address: SignupAddress | null;
}

/** The contact fields both saves of `PUT /customer-intake` send. */
export function toCustomerIntakeRequest(submission: ContactSubmission, address: SignupAddress): CustomerIntakeRequest {
  return {
    firstName: submission.firstName.trim(),
    lastName: submission.lastName.trim(),
    email: submission.email.trim(),
    phone: toE164(submission.phone),
    address,
  };
}

@Injectable({ providedIn: 'root' })
export class SignupSubmitService {
  private intake = inject(CustomerIntakeService);
  private addressStore = inject(SignupAddressStore);
  private browserMetadata = inject(TcpaBrowserMetadataService);
  private contactStore = inject(ContactFormStore);
  private rc1 = inject(Rc1EstimateStore);

  /** An intake (enrollment or waitlist) was submitted in this visit: /enrolled is open. */
  readonly isSubmitted = signal(false);

  /** A token from a repeat lookup (see `withToken`), standing in for the page's lookup token it replaced. */
  private refreshed: { replaced: string | undefined; token: string } | null = null;

  /**
   * Joins the waitlist: saves the full customer intake for the address on file, then submits it. As for
   * enrollment, only the submit carries the TCPA consent: the backend records a consent row for each request that
   * sends one, so sending it with the save too recorded it twice.
   */
  joinWaitlist(submission: ContactSubmission): Observable<void> {
    const address = submission.address;
    if (!address) return throwError(() => new Error('Add your address to join the waitlist.'));
    const tcpa = this.tcpaConsent();
    if (!tcpa) return throwError(() => new Error('The consent copy was never saved.'));
    const request = toCustomerIntakeRequest(submission, address);
    return this.withToken(address, token => this.intake.saveWaitlist(request, token)).pipe(
      switchMap(() => this.submit(tcpa)),
      map(() => undefined),
    );
  }

  /**
   * Saves the intake for a serviceable address (no TCPA consent: `submitIntake` sends it). For a new customer the
   * backend also generates the Form 50-162 signing request (no expiration date: the template no longer has one),
   * and a later save with corrected details regenerates it. `ownerRelationship` picks the capacity the form checks
   * for the signer. `submitIntake` makes it final.
   */
  enroll(
    submission: ContactSubmission, address: SignupAddress, ownerRelationship?: IntakeOwnerRelationship,
  ): Observable<CustomerIntakeResponse> {
    const request = { ...toCustomerIntakeRequest(submission, address), ...(ownerRelationship && { ownerRelationship }) };
    return this.withToken(address, token => this.intake.saveEnrollment(request, token));
  }

  /**
   * The enrollment's final submit: the intake can't be saved after. It records the TCPA consent, once: the
   * consents as they stand (copy saved when it rendered), timestamped now.
   */
  submitIntake(): Observable<SubmitCustomerIntakeResponse> {
    const tcpa = this.tcpaConsent();
    if (!tcpa) return throwError(() => new Error('The consent copy was never saved.'));
    return this.submit(tcpa);
  }

  /** Closes /enrolled again (see SignupResetService). */
  clearSubmitted(): void {
    this.isSubmitted.set(false);
  }

  /** `POST /customer-intake/submit` with the consent given; it opens /enrolled. */
  private submit(tcpa: CustomerIntakeTcpa): Observable<SubmitCustomerIntakeResponse> {
    const token = this.currentToken();
    if (!token) return throwError(() => new Error('Nothing saved to submit.'));
    return this.intake.submit({ tcpa }, token).pipe(tap(() => this.isSubmitted.set(true)));
  }

  /** The consents as they stand (copy saved when it rendered), timestamped now; null before the copy is saved. */
  private tcpaConsent(): CustomerIntakeTcpa | null {
    const consentedTo = this.contactStore.consentedTo();
    if (!consentedTo) return null;
    return { browserMetadata: this.browserMetadata.snapshot(), consentedTo, timestamp: new Date().toISOString() };
  }

  /**
   * Runs a `PUT /customer-intake` save with the address lookup's token. With no token (the lookup failed) or an
   * expired one (401), it looks the address up again for a fresh token, keeps it for the saves and submit that
   * follow, and saves once more. Each save's RC1 estimate replaces the last one (none: undefined) for /enrolled.
   */
  private withToken(
    address: SignupAddress, save: (token: string) => Observable<CustomerIntakeResponse>,
  ): Observable<CustomerIntakeResponse> {
    const replaced = this.addressStore.lookup()?.token;
    const freshToken = () => this.intake.lookup(address).pipe(map(res => {
      this.refreshed = { replaced, token: res.token };
      return res.token;
    }));

    const keepEstimate = tap((res: CustomerIntakeResponse) => this.rc1.annualPremium.set(res.rc1Estimate ?? undefined));
    const token = this.currentToken();
    if (!token) return freshToken().pipe(switchMap(save), keepEstimate);
    return save(token).pipe(
      catchError((err: HttpErrorResponse) =>
        err.status === 401 ? freshToken().pipe(switchMap(save)) : throwError(() => err)),
      keepEstimate,
    );
  }

  /** The repeat lookup's token until the page looks an address up again; then the page's. */
  private currentToken(): string | undefined {
    const token = this.addressStore.lookup()?.token;
    return this.refreshed && this.refreshed.replaced === token ? this.refreshed.token : token;
  }
}
